// What each screen is for and who can do what there (H-167 slice 1), read by "About this screen" on every page.
// Keyed by route name; a route that shows several screens adds "<route>:<view>" keys.
// Each "who" sentence follows the API's own checks (infra/rushd-console-api, rushd_core), not what the page hides.
// Who: organisation admins, a workspace's admins, its members, people in no workspace, and read-only sign-ins (viewers in
// no workspace: a viewer a workspace's admin added works as its members do; their sentence defaults in screens.ts whoSays).
// Stopping a run or deciding its plan needs a workspace that works on its project, its starter or an organisation admin,
// and a flow with no ticket is organisation admins' (2026-10-19, Amine, with Codex and Fable).
import type { ScreenHelp } from "./screens";

export const ADMINS_ONLY = "Can't open it: organisation admins only.";

export const SCREEN_CONTENT: Record<string, ScreenHelp> = {
  // ── signing in and access ──
  signin: {
    title: "Sign in",
    purpose: "Sign in to your organisation. Foodics accounts use Google; an account an admin created uses its email and password.",
    who: {
      admin: "Anyone with a Foodics Google account, or an account an admin created, signs in here. There is no self sign-up.",
      workspaceAdmin: "Anyone with a Foodics Google account, or an account an admin created, signs in here. There is no self sign-up.",
      member: "Anyone with a Foodics Google account, or an account an admin created, signs in here. There is no self sign-up.",
      noWorkspace: "Anyone with a Foodics Google account, or an account an admin created, signs in here. There is no self sign-up.",
    },
  },
  "wrong-domain": {
    title: "Organisation access",
    purpose: "You're signed in with an account that isn't part of this organisation.",
    who: {
      admin: "Sign in with your work account, or ask an organisation admin to invite this one.",
      workspaceAdmin: "Sign in with your work account, or ask an organisation admin to invite this one.",
      member: "Sign in with your work account, or ask an organisation admin to invite this one.",
      noWorkspace: "Sign in with your work account, or ask an organisation admin to invite this one.",
    },
  },
  forbidden: {
    title: "Permission",
    purpose: "The page you opened needs another role, such as organisation admin.",
    who: {
      admin: "Go back to Mission Control. An organisation admin can change someone's role if they need the page.",
      workspaceAdmin: "Go back to Mission Control, or ask an organisation admin to change your role if you need the page.",
      member: "Go back to Mission Control, or ask an organisation admin to change your role if you need the page.",
      noWorkspace: "Go back to Mission Control, or ask an organisation admin to change your role if you need the page.",
    },
  },
  "not-found": {
    title: "Not found",
    purpose: "Nothing lives at this address in Majlis. It may be an old link.",
    who: {
      admin: "Go back to Mission Control or Tickets.",
      workspaceAdmin: "Go back to Mission Control or Tickets.",
      member: "Go back to Mission Control or Tickets.",
      noWorkspace: "Go back to Mission Control or Tickets.",
    },
  },

  // ── work ──
  home: {
    title: "Mission Control",
    purpose: "Your organisation's day at a glance: runs working and finished, the next plan waiting on a person, where agents worked in the code, and schedule alerts.",
    who: {
      admin: "You see every run and can dismiss or re-enable schedule alerts. Starting a run on a ticket still needs a workspace that works on its project.",
      workspaceAdmin: "You see the runs on projects you can see, and start a run on a ticket whose project your workspace works on.",
      member: "You see the runs on projects you can see, and start a run on a ticket whose project your workspace works on.",
      noWorkspace: "You see the runs on projects you can see. Starting a run on a ticket needs a workspace that works on its project.",
    },
  },
  tickets: {
    title: "Tickets",
    purpose: "Every Jira ticket agents worked on recently, with its attempts and where each stands. Filter by running, needing a decision or failed, or open any ticket by its key.",
    who: {
      admin: "You see every ticket's runs and can open any ticket.",
      workspaceAdmin: "You see the tickets on projects you can see and open any of them.",
      member: "You see the tickets on projects you can see and open any of them.",
      noWorkspace: "You see the tickets on projects you can see. Starting work on one needs a workspace that works on its project.",
    },
  },
  ticket: {
    title: "Ticket",
    purpose: "One Jira ticket's attempts, with seven views of the selected one. The link keeps the view and the attempt, so you can share exactly what you see.",
    who: {
      admin: "You read every attempt. Starting a new attempt still needs a workspace that works on the ticket's project.",
      workspaceAdmin: "You start a new attempt when your workspace works on the ticket's project, and read every attempt you can see.",
      member: "You start a new attempt when your workspace works on the ticket's project, and read every attempt you can see.",
      noWorkspace: "You read the ticket's attempts. Starting one needs a workspace that works on the ticket's project.",
    },
  },
  "ticket:overview": {
    title: "Ticket · Overview",
    purpose: "What needs a decision: the request, the selected attempt's status and plan, every attempt, and the ticket's links in Jira, Confluence and Rushd. Answers agents gave on the ticket appear below.",
    who: {
      admin: "You can decide any plan shown here; at a run's own plan gate, approving still needs a workspace that works on the project. You can trace the links again.",
      workspaceAdmin: "When your workspace works on the ticket's project, you approve or reject its plans (a harness fix run's: its own workspace's people) and stop its runs. You can trace and hide links.",
      member: "When your workspace works on the ticket's project, you approve or reject its plans (a harness fix run's: its own workspace's people) and stop its runs. You can trace and hide links.",
      noWorkspace: "You read the plans, attempts and links. Approving a plan needs a workspace that works on the project; rejecting it or stopping the run, that or having started it.",
    },
  },
  "ticket:agents": {
    title: "Ticket · Agents",
    purpose: "Who did what in the selected attempt: its agents as a graph or a list, their state and the messages between them. Follow it live, or replay a finished attempt.",
    who: {
      admin: "You watch and replay any attempt.",
      workspaceAdmin: "You watch and replay any attempt on a project you can see.",
      member: "You watch and replay any attempt on a project you can see.",
      noWorkspace: "You watch and replay any attempt on a project you can see.",
    },
  },
  "ticket:map": {
    title: "Ticket · Code map",
    purpose: "Where this ticket touches the code across all its attempts, or one attempt's trail, on the code map.",
    who: {
      admin: "You explore it for any ticket.",
      workspaceAdmin: "You explore it for any ticket you can see.",
      member: "You explore it for any ticket you can see.",
      noWorkspace: "You explore it for any ticket you can see.",
    },
  },
  "ticket:changes": {
    title: "Ticket · Changes",
    purpose: "What the selected attempt changed and what verified it: the patch, the test evidence and the reviewer's verdict. A plan or an analysis shows its findings instead.",
    who: {
      admin: "You read it for any ticket. Nothing merges here: each pull request is reviewed and merged on GitHub.",
      workspaceAdmin: "You read it for any ticket you can see. Nothing merges here: each pull request is reviewed and merged on GitHub.",
      member: "You read it for any ticket you can see. Nothing merges here: each pull request is reviewed and merged on GitHub.",
      noWorkspace: "You read it for any ticket you can see.",
    },
  },
  "ticket:impact": {
    title: "Ticket · Impact",
    purpose: "What the selected attempt's change could reach: dependent code, routes, events and resources. A plan shows its proposed reach, and reach is not breakage.",
    who: {
      admin: "You read it for any ticket.",
      workspaceAdmin: "You read it for any ticket you can see.",
      member: "You read it for any ticket you can see.",
      noWorkspace: "You read it for any ticket you can see.",
    },
  },
  "ticket:activity": {
    title: "Ticket · Activity",
    purpose: "The selected attempt's events in order: steps, tool calls, decisions, blocked actions and errors.",
    who: {
      admin: "You read it and can stop a running attempt.",
      workspaceAdmin: "You read it, and stop a running attempt when your workspace works on its project.",
      member: "You read it, and stop a running attempt when your workspace works on its project.",
      noWorkspace: "You read it for any ticket you can see. Stopping a run needs a workspace that works on its project, unless you started it.",
    },
  },
  "ticket:context": {
    title: "Ticket · Context",
    purpose: "What the selected attempt touched in Jira, knowledge, code and GitHub, why each item is there, and what still needs a person.",
    who: {
      admin: "You read it for any ticket.",
      workspaceAdmin: "You read it for any ticket you can see.",
      member: "You read it for any ticket you can see.",
      noWorkspace: "You read it for any ticket you can see.",
    },
  },
  "run-detail": {
    title: "Run",
    purpose: "One run on its own page: its steps, the agents' stream, the code map, the patch, its reach, the analysis and its checks. A run without a Jira ticket opens here.",
    who: {
      admin: "You read and stop any run. Approving its plan, or running a ticket's run again, needs a workspace that works on its project.",
      workspaceAdmin: "You read runs on projects you can see, and stop one or decide its waiting plan when your workspace works on the project.",
      member: "You read runs on projects you can see, and stop one or decide its waiting plan when your workspace works on the project.",
      noWorkspace: "You read runs on projects you can see. Approving a plan or running one again needs a workspace on its project; you may stop or reject a run you started.",
    },
  },
  compare: {
    title: "Compare",
    purpose: "Two attempts at the same ticket side by side: tests, reach, review, cost and time, so you choose on evidence rather than the model's name.",
    who: {
      admin: "You compare any pair. Starting a new comparison on a ticket needs a workspace that works on its project.",
      workspaceAdmin: "You compare the pairs you can see, and start a new one on a ticket whose project your workspace works on.",
      member: "You compare the pairs you can see, and start a new one on a ticket whose project your workspace works on.",
      noWorkspace: "You compare the pairs you can see. Starting one needs a workspace that works on the ticket's project.",
    },
  },
  ask: {
    title: "Ask Majlis",
    purpose: "Ask about tickets, agent runs and code. Majlis picks the agent that answers, or you name one, and you can follow each answer's run step by step.",
    who: {
      admin: "Like everyone, you ask from a workspace you're in. Your conversations are yours alone: no admin reads them.",
      workspaceAdmin: "You ask your workspace's agents, the organisation's and those shared with it, rate answers and send corrections to the agent's owners.",
      member: "You ask your workspace's agents, the organisation's and those shared with it, rate answers and send corrections to the agent's owners.",
      noWorkspace: "You can't ask yet: every question runs in a workspace. Ask a workspace's admin to add you.",
    },
  },
  decisions: {
    title: "Decisions",
    purpose: "What agents asked a person to approve, such as a write through a connection or a hand-off. Each is decided once, on the exact version shown; History and Levels show the rest.",
    who: {
      admin: "You decide every waiting request and see the whole history.",
      workspaceAdmin: "You decide what your workspace's runs ask for, and writes through its own connections, when you can see every project involved.",
      member: "You decide what your workspace's runs ask for, and writes through its own connections, when you can see every project involved.",
      noWorkspace: "Nothing waits on you here: decisions belong to workspaces. You can read each agent's levels.",
    },
  },
  brief: {
    title: "Brief",
    purpose: "What the agents did over a period, for people who won't open a run: from plan to merged pull request, what reviews caught, cost, ratings, skills in use and knowledge connected.",
    who: {
      admin: "You read the whole organisation's brief.",
      workspaceAdmin: "You read it; its runs cover the projects you can see.",
      member: "You read it; its runs cover the projects you can see.",
      noWorkspace: "You read it; its runs cover the projects you can see.",
    },
  },
  architecture: {
    title: "How Majlis works",
    purpose: "The whole system in layers, what is live and what is planned, two journeys (a fix and a question), the safety layers and the infrastructure.",
    who: {
      admin: "You read all of it.",
      workspaceAdmin: "You read all of it.",
      member: "You read all of it.",
      noWorkspace: "You read all of it.",
    },
  },
  lineage: {
    title: "Everything connected",
    purpose: "Start from a ticket, a run, a pull request, a piece of code or a document, and grow the picture one hop at a time to see how they connect.",
    who: {
      admin: "You explore everything.",
      workspaceAdmin: "You explore it; tickets in projects you can't see are counted, never shown.",
      member: "You explore it; tickets in projects you can't see are counted, never shown.",
      noWorkspace: "You explore it; tickets in projects you can't see are counted, never shown.",
    },
  },
  map: {
    title: "Code map",
    purpose: "A living map of your repositories: how the code is organised, how data flows, hotspots, what a change could reach, and where agents worked lately.",
    who: {
      admin: "You explore every repository's map.",
      workspaceAdmin: "You explore the repositories of the projects you can see.",
      member: "You explore the repositories of the projects you can see.",
      noWorkspace: "You explore the repositories of the projects you can see.",
    },
  },

  // ── build ──
  connectors: {
    title: "Connectors",
    purpose: "The sources and tools your agents use: built-in ones, the organisation's connections and each workspace's own. An agent uses only the tools its definition names, and a write waits for a person.",
    who: {
      admin: "You see every connection and add one for the organisation or any workspace. Where connectors may reach and Hosted tools are yours too.",
      workspaceAdmin: "You see the built-in and organisation connections and your workspace's own, and add connections for your workspace.",
      member: "You see the built-in and organisation connections and your workspace's own. Adding one is for a workspace's admins.",
      noWorkspace: "You see the built-in and organisation connections. A workspace's own connections are shown to its members only.",
    },
  },
  connector: {
    title: "Connection",
    purpose: "One connection: who owns it, its state and what it needs, with tabs for its setup or overview, tools, usage, activity and settings. Built-in connectors come with Majlis and need no setup.",
    who: {
      admin: "You manage every connection: test it, review its tools, save its credential, activate it alone (the audit records it), switch it off or retire it.",
      workspaceAdmin: "You manage your workspace's connections, including which alarms start its agents. One that can write needs another of its admins to request activation first.",
      member: "You read your workspace's and the organisation's connections: their tools, who uses them and everything they did. Only admins change them.",
      noWorkspace: "You read the built-in and organisation connections and everything they did. A workspace's own connections are for its members.",
    },
  },
  "connector:new": {
    title: "Add a connection",
    purpose: "Choose what to connect: CloudWatch, a database, a remote MCP server, or an MCP server Majlis runs on AgentCore. Each says what it gives your agents, how Majlis signs in and what you need.",
    who: {
      admin: "You add any kind of connection, for the organisation or for any workspace.",
      workspaceAdmin: "You add any kind of connection for your workspace. Its agents use it; other workspaces only by asking one of them.",
      member: "You can read the choices; your workspace's admins add its connections.",
      noWorkspace: "You can read the choices; an organisation admin or a workspace's admin adds connections.",
    },
  },
  "connector:new:cloudwatch": {
    title: "Connect CloudWatch",
    purpose: "Connect a team's CloudWatch: whose it is and its AWS account and region, a read-only role your engineers create from Majlis's template, what agents may read, then a test and activation.",
    who: {
      admin: "You connect a CloudWatch for the organisation, where every workspace's agents may use it, or for any workspace.",
      workspaceAdmin: "You connect your workspace's CloudWatch and activate it yourself: it only reads.",
      member: "You can't add one: ask an admin of your workspace.",
      noWorkspace: "You can't add one: an organisation admin or a workspace's admin does.",
    },
  },
  "connector:new:database": {
    title: "Connect a database",
    purpose: "Connect a team's MySQL database with a user that can only read, through your bastion or directly. Paste its password once, test it, choose the tables and columns agents see, then activate.",
    who: {
      admin: "You connect a database for the organisation or for any workspace.",
      workspaceAdmin: "You connect your workspace's database and activate it yourself: its agents only read, with checked queries.",
      member: "You can't add one: ask an admin of your workspace.",
      noWorkspace: "You can't add one: an organisation admin or a workspace's admin does.",
    },
  },
  "connector:new:mcp": {
    title: "Add a remote MCP server",
    purpose: "Add an MCP server by its HTTPS address and a bearer token or API key, kept write-only. Majlis's operators must have allowed the address; you then review each tool it finds before activating.",
    who: {
      admin: "You add one for the organisation or any workspace, and activate it alone, writes included; the audit records it.",
      workspaceAdmin: "You add one for your workspace, read-only unless you choose otherwise. Activating one that writes needs another of your workspace's admins.",
      member: "You can't add one: ask an admin of your workspace.",
      noWorkspace: "You can't add one: an organisation admin or a workspace's admin does.",
    },
  },
  "connector:new:agentcore": {
    title: "Add an MCP server on AgentCore",
    purpose: "Add a hosted tool Majlis runs on AgentCore: Majlis signs in with its own role, so there is nothing to paste. Its address must be allowed first; then review its tools and activate it.",
    who: {
      admin: "You add one for the organisation or any workspace, and activate it alone, writes included; the audit records it.",
      workspaceAdmin: "You add one for your workspace, read-only unless you choose otherwise. Activating one that writes needs another of your workspace's admins.",
      member: "You can't add one: ask an admin of your workspace.",
      noWorkspace: "You can't add one: an organisation admin or a workspace's admin does.",
    },
  },
  "endpoint-rule": {
    title: "Where connectors may reach",
    purpose: "The organisation's rule for connectors: always HTTPS to a public address, a block list that always wins, and any public domain or allowed domains only. It also lists every domain a connector reached.",
    who: {
      admin: "You set the rule; a change applies at once to every read and every approved write.",
      workspaceAdmin: ADMINS_ONLY,
      member: ADMINS_ONLY,
      noWorkspace: ADMINS_ONLY,
    },
  },
  "hosted-tools": {
    title: "Hosted tools",
    purpose: "Tools a team writes and Majlis runs: each release is built from a pinned commit of the team's repository, and runs only once an organisation admin approves it.",
    who: {
      admin: "You approve a release to make it run, and retire a tool.",
      workspaceAdmin: ADMINS_ONLY,
      member: ADMINS_ONLY,
      noWorkspace: ADMINS_ONLY,
    },
  },
  "telemetry-project": {
    title: "CloudWatch · project",
    purpose: "One project's mapping on the built-in CloudWatch connector: which AWS account and region, and which dashboards, logs, metrics and alarms its agents may read. It shows what agents were refused for want of one.",
    who: {
      admin: "You set the account and what agents may read, test the connection and pick names from the account.",
      workspaceAdmin: ADMINS_ONLY,
      member: ADMINS_ONLY,
      noWorkspace: ADMINS_ONLY,
    },
  },
  agents: {
    title: "Agents",
    purpose: "Every agent in the organisation by its @handle, grouped by workspace: what it's for, its active version, its model and how often it ran.",
    who: {
      admin: "You create agents for the organisation or for any workspace, and open any agent to change it.",
      workspaceAdmin: "You create agents for your workspace (they belong to it) and open any agent to read it.",
      member: "You read every agent and try those your workspace may use. Creating agents is for admins.",
      noWorkspace: "You read every agent. Trying one needs a workspace that may use it.",
    },
  },
  agent: {
    title: "Agent",
    purpose: "One agent: its versions and its definition, edited in the composer, a form or YAML and saved as a draft. Activate a version, try it, choose its Jira boards, schedule it and see what it learnt.",
    who: {
      admin: "You edit any agent and activate any version, your own drafts too. You switch agents off or on, choose their boards and schedule them.",
      workspaceAdmin: "You edit your workspace's agents and switch them off. A version that lets one do more needs another admin to activate it, not its author.",
      member: "You read its versions and definition, and try it if your workspace may use it. Editing and activating are for admins.",
      noWorkspace: "You read its versions and definition. Trying it needs a workspace that may use it.",
    },
  },
  skills: {
    title: "Skills",
    purpose: "Reviewed instructions agents follow, such as how a team reads a ticket. A draft becomes usable only when someone other than its author reviews it, and an agent names the exact version.",
    who: {
      admin: "You write any skill and review any draft, except your own: someone else reviews it.",
      workspaceAdmin: "You write your workspace's skills and review its drafts that someone else wrote.",
      member: "You read every skill and its versions. Admins write and review them.",
      noWorkspace: "You read every skill and its versions.",
    },
  },
  notes: {
    title: "Notes",
    purpose: "What a team wants its agents to know about a Jira project. Every run on the project reads its enabled notes; a workspace's own notes reach only its own runs.",
    who: {
      admin: "You read and write every note on every project.",
      workspaceAdmin: "You write project-wide notes on projects your workspace owns, and your workspace's own notes on projects it works on.",
      member: "You read a project's shared notes and your workspace's own. Its admins write them.",
      noWorkspace: "You read the shared notes on projects you can see.",
    },
  },
  readiness: {
    title: "Repo readiness",
    purpose: "How ready each repository is for agents to code in it: the readiness ladder, the checks each one passes or misses, and which can have a Majlis agent of its own.",
    who: {
      admin: "You see every repository.",
      workspaceAdmin: "You see the repositories of the projects you can see.",
      member: "You see the repositories of the projects you can see.",
      noWorkspace: "You see the repositories of the projects you can see.",
    },
  },
  flows: {
    title: "All flows",
    purpose: "Every workflow Majlis can run, each with a form built from its inputs. Pick one to preview its inputs, then open it to run it.",
    who: {
      admin: "You browse every flow, run it, and schedule it or save its settings.",
      workspaceAdmin: "You browse every flow and open one to run it. Schedules and saved settings are organisation admins'.",
      member: "You browse every flow and open one to run it. Schedules and saved settings are organisation admins'.",
      noWorkspace: "You browse every flow and preview its inputs.",
    },
  },
  run: {
    title: "Run a flow",
    purpose: "Run one workflow with a form built from its inputs, starting from a saved configuration if you like. A Jira ticket fills in the repository from its project's mapping.",
    who: {
      admin: "You run any flow, those with no ticket included. One tied to a Jira ticket still needs a workspace that works on its project.",
      workspaceAdmin: "You run a flow on a ticket whose project your workspace works on. A flow with no ticket is for organisation admins.",
      member: "You run a flow on a ticket whose project your workspace works on. A flow with no ticket is for organisation admins.",
      noWorkspace: "You can't start one: a ticket's flow needs a workspace on its project, and one with no ticket is for organisation admins.",
    },
  },

  // ── govern ──
  bindings: {
    title: "Bindings",
    purpose: "Narrow, per workspace, which of a connector's tools an agent uses. A binding never adds a tool, and the hub follows within minutes.",
    who: {
      admin: "You set bindings for every workspace or for one workspace.",
      workspaceAdmin: "You set bindings for your own workspace.",
      member: "You read every binding. Your workspace's admins set them.",
      noWorkspace: "You read every binding.",
      viewer: "Can't read it until a workspace's admin adds you to their workspace.",
    },
  },
  budgets: {
    title: "Budgets",
    purpose: "Spending limits for Majlis's agents at list price, per day or month: for the organisation, a workspace, a project or an agent. It shows this month's spend and usage still held.",
    who: {
      admin: "You set, change and delete budgets, and release a held run's usage with a reason.",
      workspaceAdmin: ADMINS_ONLY,
      member: ADMINS_ONLY,
      noWorkspace: ADMINS_ONLY,
    },
  },
  configs: {
    title: "Configurations",
    purpose: "Saved settings for running and scheduling a workflow, such as its model, budget and review, so every run or schedule can reuse them.",
    who: {
      admin: "You create, change and delete configurations.",
      workspaceAdmin: "You read every configuration and start runs from one. Organisation admins create and change them.",
      member: "You read every configuration and start runs from one. Organisation admins create and change them.",
      noWorkspace: "You read every configuration. Organisation admins create and change them.",
    },
  },
  schedules: {
    title: "Schedules",
    purpose: "Workflows that run on a timetable with saved settings; an agent's own schedules are on its page. A schedule whose recent runs keep failing is switched off and raises an alert here.",
    who: {
      admin: "You create schedules, pause, enable or delete them, and dismiss or re-enable their alerts.",
      workspaceAdmin: "You see every schedule and its alerts. Organisation admins create and change them.",
      member: "You see every schedule and its alerts. Organisation admins create and change them.",
      noWorkspace: "You see every schedule and its alerts. Organisation admins create and change them.",
    },
  },

  // ── workspaces ──
  workspaces: {
    title: "Workspaces",
    purpose: "Each team's home in Majlis: its people, the Jira projects it owns or that are shared with it, and the agents it uses.",
    who: {
      admin: "You create workspaces and open any of them.",
      workspaceAdmin: "You see every workspace's name and open your own.",
      member: "You see every workspace's name and open your own.",
      noWorkspace: "You see every workspace's name. An admin of one can add you to it.",
    },
  },
  workspace: {
    title: "Workspace",
    purpose: "One workspace: the Jira projects it owns and those shared with it, the agents it may use, its people and its connections. Its Learning and Sirah open from here.",
    who: {
      admin: "You open any workspace, rename it, add or remove its people and share the projects it owns.",
      workspaceAdmin: "You add people by email (even before their first sign-in), remove them, make them admins, and share your projects with other workspaces.",
      member: "You see its projects, agents and people, and can make it the workspace you work in. Its admins change its people.",
      noWorkspace: "Can't open one: a workspace's page is for its members and organisation admins.",
    },
  },
  learning: {
    title: "Learning",
    purpose: "What a workspace's agents learnt from its people's corrections: reviewed lessons that guide their answers, never their tools or permissions.",
    who: {
      admin: "You review any workspace's corrections and write, draft, approve, reject or retire its lessons.",
      workspaceAdmin: "You review your workspace's corrections and write, draft, approve, reject or retire its agents' lessons.",
      member: "You read the approved and retired lessons, who taught them and how often they were used.",
      noWorkspace: "Can't open it: a workspace's lessons are for its members and organisation admins.",
    },
  },
  sirah: {
    title: "Sirah",
    purpose: "A workspace's reviewed memory of how its team works, built from the Slack channels approved for it, and never about individual people. Its published versions can guide its own agents.",
    who: {
      admin: "You do everything its admins do, in any workspace.",
      workspaceAdmin: "You choose its approved channels, build the memory, review items, switch off noisy threads and publish versions.",
      member: "You read it, preview how the team would handle a situation, and ask for an item to be removed.",
      noWorkspace: "Can't open it: a workspace's Sirah is for its members and organisation admins.",
    },
  },
  "sirah-channels": {
    title: "Sirah channels",
    purpose: "Which Slack channels may feed each workspace's Sirah. The facts come from the organisation's channel inventory, pulled nightly; each approval names a workspace and the consent it rests on.",
    who: {
      admin: "You set up the inventory, pull it now, and approve or revoke a channel for a workspace.",
      workspaceAdmin: ADMINS_ONLY,
      member: ADMINS_ONLY,
      noWorkspace: ADMINS_ONLY,
    },
  },
  projects: {
    title: "Jira projects",
    purpose: "Every Jira project you can see, the workspace that owns it and those it's shared with. Its owner works on it and administers it; workspaces it's shared with work on it too.",
    who: {
      admin: "You add projects for any workspace, assign or move a project's owner, rename projects and share them.",
      workspaceAdmin: "You add projects to your workspace, rename the ones it owns and share them with other workspaces.",
      member: "You see who owns each project and which workspaces work on it. Your workspace's admins change them.",
      noWorkspace: "You see who owns each project and which workspaces work on it.",
    },
  },
  "project-events": {
    title: "New tickets",
    purpose: "Which new tickets in a Jira project start an agent, chosen from those its owning workspace may use, and what its new tickets did lately.",
    who: {
      admin: "You choose what new tickets start, or switch it off, on any project.",
      workspaceAdmin: "You choose what new tickets start on the projects your workspace owns.",
      member: "You read the rule and what new tickets started. Your workspace's admins set it.",
      noWorkspace: "You read the rule and what new tickets started on projects you can see.",
    },
  },
  mappings: {
    title: "Mappings",
    purpose: "Which GitHub repository each Jira project's runs work in, its base branch and default configuration, and whether @majlis on its tickets starts a run.",
    who: {
      admin: "You add, change and delete mappings. Connect a project does the same, with its checks.",
      workspaceAdmin: ADMINS_ONLY,
      member: ADMINS_ONLY,
      noWorkspace: ADMINS_ONLY,
    },
  },
  onboarding: {
    title: "Connect a project",
    purpose: "Give agents a place to start: install the Majlis GitHub App on repositories and connect each to its Jira project, turn on @majlis from Jira, set defaults, and map CloudWatch.",
    who: {
      admin: "You install the app, connect repositories to their projects, connect Jira and set up CloudWatch.",
      workspaceAdmin: ADMINS_ONLY,
      member: ADMINS_ONLY,
      noWorkspace: ADMINS_ONLY,
    },
  },
  "github-app-created": {
    title: "GitHub App",
    purpose: "Where GitHub sends you after you create the Majlis GitHub App. Majlis checks the app's owner and keeps its key in AWS; the console never sees it.",
    who: {
      admin: "You finish creating the app here, then install it on your repositories.",
      workspaceAdmin: ADMINS_ONLY,
      member: ADMINS_ONLY,
      noWorkspace: ADMINS_ONLY,
    },
  },
};
